import * as React from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import moment from 'moment/moment';
import { useParams } from 'react-router';
import { useEffect } from 'react';
import { getApi } from 'views/services/api';
import { Container, Card, CardContent, Stack, Chip, Divider, alpha, useTheme, Avatar } from '@mui/material';
import {
  Hotel as HotelIcon,
  BugReport as BugIcon,
  Support as SupportIcon,
  Build as TechnicalIcon,
  Lightbulb as FeatureIcon,
  CheckCircle as ResolvedIcon,
  Schedule as PendingIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';

export default function AdminComplaintDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();

  //function for fetching property data based on the property id
  // ----------------------------------------------------------------------
  const [complaintData, setComplaintData] = useState([]);
  const params = useParams();
  const complaintId = params.id;
  console.log('complaintId ==>', complaintId);

  const fetchcomplaintData = async () => {
    try {
      console.log(' url hit ==>', `api/complaint/view/${complaintId}`);
      const response = await getApi(`api/complaint/view/${complaintId}`);
      console.log('here is the response =>', response);
      setComplaintData(response?.data?.mergedData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchcomplaintData();
  }, []);

  // Función para obtener el icono según el tipo
  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'bug':
        return <BugIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />;
      case 'feature':
        return <FeatureIcon sx={{ fontSize: 24, color: theme.palette.info.main }} />;
      case 'support':
        return <SupportIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />;
      case 'technical':
        return <TechnicalIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />;
      default:
        return <BugIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />;
    }
  };

  // Función para obtener el color del tipo
  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'bug':
        return 'error';
      case 'feature':
        return 'info';
      case 'support':
        return 'warning';
      case 'technical':
        return 'primary';
      default:
        return 'primary';
    }
  };

  // Función para renderizar campo de información
  const renderInfoField = (label, value, icon) => (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={600} color="secondary">
          {label}
        </Typography>
      </Stack>
      <Typography
        variant="body1"
        sx={{
          ml: 5,
          color: value && value !== 'N/A' ? 'text.primary' : 'text.secondary',
          fontWeight: value && value !== 'N/A' ? 500 : 400
        }}
      >
        {value || 'No especificado'}
      </Typography>
    </Box>
  );

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: 'white',
            borderRadius: 3
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Detalles Administrativos del Reporte
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Vista completa del reporte de soporte #{complaintId}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Volver
            </Button>
          </Stack>
        </Paper>

        <TabContext value="1">
          <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <TabList
                textColor="secondary"
                indicatorColor="secondary"
                sx={{
                  px: 3,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem'
                  }
                }}
              >
                <Tab label="Información Completa del Reporte" value="1" />
              </TabList>
            </Box>

            <TabPanel value="1" sx={{ p: 0 }}>
              <Box sx={{ p: 4 }}>
                {/* Hotel and Report Header */}
                <Box sx={{ mb: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha(theme.palette.secondary.main, 0.1),
                        color: theme.palette.secondary.main,
                        fontSize: '1.5rem',
                        fontWeight: 700
                      }}
                    >
                      {complaintData.hotelName?.charAt(0)?.toUpperCase() || 'H'}
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        {complaintData.hotelName || 'Hotel No Identificado'}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Chip
                          label={complaintData.status === true ? 'Resuelto' : 'Pendiente'}
                          color={complaintData.status === true ? 'success' : 'warning'}
                          variant="filled"
                          icon={complaintData.status === true ? <ResolvedIcon /> : <PendingIcon />}
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip
                          label={complaintData.type || 'General'}
                          color={getTypeColor(complaintData.type)}
                          variant="outlined"
                          sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </Box>

                <Divider sx={{ mb: 4 }} />

                {/* Information Grid */}
                <Grid container spacing={4}>
                  {/* Hotel Information */}
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
                      }}
                    >
                      <Typography variant="h6" fontWeight={600} color="info.main" gutterBottom sx={{ mb: 3 }}>
                        Información del Hotel
                      </Typography>

                      {renderInfoField(
                        'Nombre del Hotel',
                        complaintData.hotelName,
                        <HotelIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                      )}

                      {renderInfoField(
                        'Correo Electrónico',
                        complaintData.email,
                        <EmailIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                      )}

                      {renderInfoField(
                        'Número de Contacto',
                        complaintData.contact,
                        <PhoneIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                      )}

                      {renderInfoField(
                        'Dirección',
                        complaintData.address,
                        <LocationIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                      )}
                    </Card>
                  </Grid>

                  {/* Report Information */}
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}
                    >
                      <Typography variant="h6" fontWeight={600} color="primary" gutterBottom sx={{ mb: 3 }}>
                        Información del Reporte
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <CategoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                          </Box>
                          <Typography variant="subtitle1" fontWeight={600} color="primary">
                            Tipo de Reporte
                          </Typography>
                        </Stack>
                        <Box sx={{ ml: 5, display: 'flex', alignItems: 'center', spacing: 1 }}>
                          {getTypeIcon(complaintData.type)}
                          <Typography variant="body1" fontWeight={500} sx={{ ml: 1, textTransform: 'capitalize' }}>
                            {complaintData.type || 'No especificado'}
                          </Typography>
                        </Box>
                      </Box>

                      {renderInfoField(
                        'Fecha de Creación',
                        complaintData.createdDate ? moment(complaintData.createdDate).format('DD/MM/YYYY [a las] HH:mm') : null,
                        <CalendarIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      )}

                      {complaintData.createdDate && (
                        <Box sx={{ ml: 5, mt: -2, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Hace {moment(complaintData.createdDate).fromNow()}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 3 }}>
                        <DescriptionIcon sx={{ fontSize: 20, color: theme.palette.warning.main, mt: 0.5 }} />
                        <Typography variant="h6" fontWeight={600} color="warning.main">
                          Descripción Detallada del Problema
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          p: 3,
                          backgroundColor: alpha(theme.palette.grey[500], 0.05),
                          borderRadius: 2,
                          minHeight: 120,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap',
                            fontSize: '1rem'
                          }}
                        >
                          {complaintData.description || 'No se proporcionó una descripción detallada del problema.'}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>

                {/* Administrative Actions */}
                <Box sx={{ mt: 4 }}>
                  <Card
                    sx={{
                      p: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(
                        theme.palette.secondary.main,
                        0.05
                      )} 100%)`,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                      borderRadius: 2
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <AdminIcon sx={{ fontSize: 28, color: theme.palette.secondary.main }} />
                      <Box>
                        <Typography variant="h6" fontWeight={600} color="secondary.main">
                          Vista Administrativa
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Esta es una vista completa del reporte con información del hotel y detalles técnicos para el equipo de soporte
                          administrativo.
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Box>
              </Box>
            </TabPanel>
          </Card>
        </TabContext>
      </Container>
    </>
  );
}
